<template>
  <section id="hero">
    <!-- Hero -->
    <v-container fluid class="hero-section">
      <div class="hero-glow" aria-hidden="true"></div>

      <v-row align="center" justify="center" class="hero-row">
        <v-col cols="12" md="10" lg="8" class="text-center px-6">
          <!-- Marca -->
          <img
            src="@/assets/salvia_lockup.svg"
            alt="Salvia"
            class="hero-logo mb-8"
          />

          <div class="hero-badge mb-6">
            <v-icon small color="primary" class="mr-1">mdi-shield-check</v-icon>
            <span>Plataforma segura e confiável</span>
          </div>

          <h1 class="hero-title mb-5">
            Gestão inteligente de<br class="d-none d-sm-block" />
            tratamentos de saúde
          </h1>

          <p class="hero-subtitle mb-8 mx-auto">
            Transforme a forma como você acompanha seus pacientes. Otimize o
            tratamento com uma plataforma simples, eficaz e segura.
          </p>

          <div class="d-flex align-center justify-center flex-wrap" style="gap: 14px">
            <v-btn
              rounded
              large
              color="primary"
              dark
              depressed
              @click="$vuetify.goTo('#features')"
              class="btn-primary-custom"
            >
              Comece agora
              <v-icon class="ml-2" small>mdi-arrow-right</v-icon>
            </v-btn>
            <v-btn
              rounded
              large
              outlined
              @click="$vuetify.goTo('#about')"
              class="btn-outline-custom"
            >
              Saiba mais
            </v-btn>
          </div>

          <!-- Indicador de rolagem -->
          <div class="hero-scroll mt-12" @click="$vuetify.goTo('#features')">
            <v-icon color="primary">mdi-chevron-down</v-icon>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- Features -->
    <v-container fluid id="features" class="features-section py-16">
      <v-row align="center" justify="center">
        <v-col cols="10" lg="8">
          <div class="text-center mb-12">
            <p class="section-overline mb-2">RECURSOS</p>
            <h2 class="section-title mb-4">A Tecnologia a Serviço da Saúde</h2>
            <p class="section-subtitle mx-auto" style="max-width: 560px">
              Aprimore seu atendimento com as ferramentas certas para cada etapa
              do tratamento.
            </p>
          </div>

          <v-row justify="center">
            <v-col
              cols="12"
              sm="6"
              md="4"
              class="mb-4"
              v-for="(feature, i) in features"
              :key="i"
            >
              <v-hover v-slot:default="{ hover }">
                <v-card
                  class="feature-card pa-6"
                  flat
                  :elevation="hover ? 6 : 0"
                  :class="{ 'feature-card--hover': hover }"
                >
                  <div class="feature-icon-wrapper mb-5">
                    <v-img
                      :src="feature.img"
                      max-width="56px"
                      class="d-block"
                    />
                  </div>
                  <h3 class="feature-card-title mb-3">{{ feature.title }}</h3>
                  <p class="feature-card-text">{{ feature.text }}</p>
                </v-card>
              </v-hover>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>

    <!-- Depoimentos -->
    <v-container fluid class="testimonials-section py-16">
      <v-row align="center" justify="center">
        <v-col cols="10" lg="8">
          <div class="text-center mb-12">
            <p class="section-overline mb-2">DEPOIMENTOS</p>
            <h2 class="section-title mb-4">O Que Nossos Usuários Dizem</h2>
            <p class="section-subtitle">
              Profissionais de saúde confiam no Salvia para transformar seu dia
              a dia.
            </p>
          </div>

          <v-row justify="center">
            <v-col
              cols="12"
              sm="6"
              md="4"
              class="mb-4"
              v-for="(testimonial, i) in testimonials"
              :key="i"
            >
              <v-card class="testimonial-card pa-6" flat>
                <v-icon color="primary" class="mb-4" size="28"
                  >mdi-format-quote-open</v-icon
                >
                <p class="testimonial-text mb-5">{{ testimonial.text }}</p>
                <div class="d-flex align-center">
                  <v-avatar color="primarySurface" size="40" class="mr-3">
                    <span
                      class="teal--text text--darken-1 font-weight-medium"
                      style="font-size: 14px"
                    >
                      {{ testimonial.initials }}
                    </span>
                  </v-avatar>
                  <div>
                    <p class="testimonial-name mb-0">{{ testimonial.name }}</p>
                    <p class="testimonial-role mb-0">{{ testimonial.role }}</p>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>

    <!-- CTA -->
    <v-container fluid class="cta-section py-16" id="cta">
      <v-row align="center" justify="center">
        <v-col cols="10" lg="6" class="text-center">
          <h2 class="cta-title mb-4">Pronto para Transformar sua Prática?</h2>
          <p class="cta-subtitle mb-8">
            Comece a usar o Salvia hoje mesmo e ofereça um atendimento de
            excelência aos seus pacientes.
          </p>
          <v-btn
            rounded
            large
            color="primary"
            dark
            depressed
            @click="$vuetify.goTo('#signup')"
            class="btn-primary-custom"
          >
            Criar Minha Conta
            <v-icon class="ml-2" small>mdi-arrow-right</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </section>
</template>

<script>
export default {
  data() {
    return {
      features: [
        {
          img: require("@/assets/undraw_medical-care_7m9g.svg"),
          title: "Gestão de Pacientes",
          text: "Organize o histórico de tratamentos, exames e evolução dos seus pacientes em um único lugar.",
        },
        {
          img: require("@/assets/undraw_medical-research_pze7.svg"),
          title: "Tratamentos Personalizados",
          text: "Crie planos de tratamento personalizados com base nas necessidades específicas de cada paciente.",
        },
        {
          img: require("@/assets/undraw_detailed-analysis_w5a8.svg"),
          title: "Relatórios e Análises",
          text: "Obtenha relatórios detalhados e visualize gráficos que ajudam na tomada de decisões.",
        },
      ],
      testimonials: [
        {
          name: "Dra. Ana Souza",
          role: "Nutricionista",
          initials: "AS",
          text: "O Salvia facilitou demais o meu trabalho! Agora consigo acompanhar todos os meus pacientes em tempo real e criar planos alimentares mais eficazes.",
        },
        {
          name: "Dr. João Silva",
          role: "Médico",
          initials: "JS",
          text: "A plataforma é intuitiva e me ajuda a organizar os tratamentos de forma ágil e segura. Meus pacientes adoram a facilidade.",
        },
        {
          name: "Dra. Mariana Oliveira",
          role: "Endocrinologista",
          initials: "MO",
          text: "Com o Salvia, minha rotina está muito mais organizada. É uma ferramenta indispensável para tratamentos personalizados.",
        },
      ],
    };
  },
};
</script>

<style scoped>
/* ===== Hero ===== */
.hero-section {
  position: relative;
  overflow: hidden;
  background: linear-gradient(180deg, #f7f4ee 0%, #ffffff 100%);
  color: var(--ds-ink);
  padding: 96px 0 72px;
}

/* Brilho pinheiro suave atrás da marca — dá profundidade sem poluir. */
.hero-glow {
  position: absolute;
  top: -220px;
  left: 50%;
  transform: translateX(-50%);
  width: 900px;
  height: 620px;
  background: radial-gradient(
    ellipse at center,
    rgba(62, 142, 115, 0.16) 0%,
    rgba(62, 142, 115, 0.05) 45%,
    transparent 70%
  );
  pointer-events: none;
}

.hero-row {
  position: relative;
  z-index: 1;
}

.hero-logo {
  width: 100%;
  max-width: 340px;
  height: auto;
  display: block;
  margin-inline: auto;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  background: var(--ds-primary-surface);
  border: 1px solid var(--ds-primary-border);
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  color: var(--ds-primary-deep);
}

.hero-title {
  font-size: clamp(2.1rem, 5vw, 3.4rem);
  font-weight: 700;
  line-height: 1.12;
  letter-spacing: -0.02em;
  color: var(--ds-secondary);
  text-wrap: balance;
}

.hero-subtitle {
  font-size: clamp(1rem, 2vw, 1.2rem);
  font-weight: 300;
  line-height: 1.6;
  color: #5a6b62;
  max-width: 620px;
}

.btn-primary-custom {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.2px;
  padding: 0 28px !important;
  box-shadow: 0 6px 18px rgba(47, 115, 89, 0.28) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.btn-primary-custom:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(47, 115, 89, 0.34) !important;
}

.btn-outline-custom {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.2px;
  padding: 0 26px !important;
  color: var(--ds-primary-deep) !important;
  border-color: #b7ccc0 !important;
}
.btn-outline-custom:hover {
  background-color: var(--ds-primary-surface) !important;
}

.hero-scroll {
  cursor: pointer;
  animation: heroBounce 2.2s infinite;
  opacity: 0.7;
}
@keyframes heroBounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(6px); }
}
@media (prefers-reduced-motion: reduce) {
  .hero-scroll { animation: none; }
}

.section-overline {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 2px;
  color: var(--ds-primary);
  text-transform: uppercase;
  margin-bottom: 0;
}

.section-title {
  font-size: 2.25rem;
  font-weight: 600;
  color: var(--ds-ink);
  letter-spacing: -0.3px;
  line-height: 1.2;
}

.section-subtitle {
  font-size: 1.05rem;
  font-weight: 300;
  color: var(--ds-gray);
  line-height: 1.6;
}

/* ===== Features ===== */
.features-section {
  background-color: var(--ds-surface-white);
}

.feature-card {
  background: var(--ds-gray-50);
  border-radius: 12px !important;
  border: 1px solid #f0f0f0;
  height: 100%;
  transition: all 0.3s ease;
}

.feature-card--hover {
  transform: translateY(-4px);
  border-color: transparent;
}

.feature-icon-wrapper {
  width: 72px;
  height: 72px;
  border-radius: 16px;
  background: var(--ds-blue-100);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
}

.feature-card-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--ds-ink);
}

.feature-card-text {
  font-size: 0.9rem;
  font-weight: 300;
  color: #888;
  line-height: 1.65;
  margin-bottom: 0;
}

/* ===== Testimonials ===== */
.testimonials-section {
  background-color: var(--ds-gray-bg);
}

.testimonial-card {
  background: var(--ds-surface-white);
  border-radius: 12px !important;
  border: 1px solid #eee;
  height: 100%;
}

.testimonial-text {
  font-size: 0.925rem;
  color: var(--ds-gray-mid);
  line-height: 1.7;
  font-style: italic;
}

.testimonial-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--ds-ink);
}

.testimonial-role {
  font-size: 0.8rem;
  color: var(--ds-gray-light);
}

/* ===== CTA ===== */
.cta-section {
  background-color: #111111;
  color: var(--ds-white);
}

.cta-title {
  font-size: 2.25rem;
  font-weight: 600;
  letter-spacing: -0.3px;
}

.cta-subtitle {
  font-size: 1.05rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.6;
}

/* ===== Responsive ===== */
@media (max-width: 960px) {
  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.1rem;
  }

  .section-title {
    font-size: 1.75rem;
  }

  .cta-title {
    font-size: 1.75rem;
  }
}
</style>
